/**
 * Move an indicator's lines into another pane, or out into a new one.
 *
 * The owner-approved mock (2026-10-03): a **Move to** menu on every own-pane row
 * of the indicator popup — the price pane, each other pane, *New pane* — with an
 * **Own scale** checkbox. Three "% Above MA" lines in one pane is the case it
 * exists for.
 *
 * | target | scale |
 * |---|---|
 * | an existing pane, **Share** (default) | the target pane's first axis — the lines share its scale |
 * | an existing pane, **Hidden** | a copy of the lines' own axis, added to the target pane and not drawn |
 * | an existing pane, **Left** / **Right** | the same copy, drawn on that side |
 * | New pane | a copy of the lines' own axis, drawn; the scale choice does not apply |
 *
 * The **Own scale** checkbox became the four-way choice on 2026-10-05 (owner): a
 * Rank in the price pane wants its own 0–100 scale *and* no axis eating the
 * candles' width, which a checkbox could not say. `setPlotsScale` is the same
 * choice for lines already where they are — the row's ⚙ — and adding a Rank
 * uses `movePlotsToPane` with `hidden` to land it on the price pane.
 *
 * Pure, like `add-indicator.ts`: `mintId` is injected.
 *
 * - **An indicator pane emptied by the move goes** — `removeIndicator`'s rule:
 *   an empty pane is dead height with no way to fill it. The price and volume
 *   panes are never removed.
 * - **An axis left with no plot on it goes too**, so a pane does not keep a
 *   scale for lines that moved away.
 * - **A signal's `anchorRef` is left as it is.** If it named a line that is now
 *   in another pane, `resolveAnchorRef` already degrades it to the candles —
 *   the same answer as a deleted line.
 */

import type { AxisInfo, ChartGroup, Pane, Plot } from "@qufolio/model";

export const NEW_PANE = "new";

/**
 * Indicator on indicator (owner, 2026-10-06): `obv~sma20` is a 20-bar SMA of
 * the `obv` column, computed by qv-data (`qf/smoothed.py`). SMA, EMA, WMA and
 * HMA (the last two 2026-10-19), one level — the server parses exactly this and drops anything else.
 *
 * Here rather than in `add-indicator.ts` because both modules need it and that
 * one already imports this.
 */
export const SMOOTH_KINDS = ["sma", "ema", "wma", "hma"] as const;
export type SmoothKind = (typeof SMOOTH_KINDS)[number];

const SMOOTHED = /^([^~]+)~(sma|ema|wma|hma)(\d{1,3})$/;

export function splitSmoothed(
  alias: string | undefined,
): { source: string; kind: SmoothKind; length: number } | undefined {
  const found = alias ? SMOOTHED.exec(alias) : null;
  if (!found) return undefined;
  const length = Number(found[3]);
  if (length < 2 || length > 500) return undefined;
  return { source: found[1]!, kind: found[2] as SmoothKind, length };
}

/**
 * `ids` plus every smoother in `pane` whose source is one of their aliases — a
 * smoother draws on its source's scale, so it moves and re-scales with it.
 */
function withSmoothers(pane: Pane, ids: ReadonlySet<string>): Set<string> {
  const plots = pane.plots ?? [];
  const sources = new Set(plots.filter((p) => ids.has(p.id)).map((p) => p.outputAlias));
  const out = new Set(ids);
  for (const p of plots) {
    const found = splitSmoothed(p.outputAlias);
    if (found && sources.has(found.source)) out.add(p.id);
  }
  return out;
}

export interface MoveToPane {
  /** The plots to move — every plot of one indicator row. */
  plotIds: readonly string[];
  /** A pane id, or {@link NEW_PANE}. */
  target: string;
  /** How the lines are scaled in the target pane. Ignored for a new pane. */
  scale: PlotScale;
  /**
   * The indicator's own axis as the catalog describes it (`axisFor`) — range,
   * format, scale. Used when the lines have no axis of their own to carry,
   * because they shared the source pane's: copying *that* would hand a Rank the
   * price axis's auto range instead of its 0–100 (owner, 2026-10-06).
   */
  base?: AxisInfo;
}

/**
 * The lines' scale inside a pane they share: on the pane's own (`share`), or on
 * one of their own — undrawn (`hidden`), or drawn on the `left` or `right`.
 */
export type PlotScale = "share" | "hidden" | "left" | "right";

/** An axis of the lines' own, shaped by `scale` — never called with `share`. */
function shapedAxis(base: AxisInfo | undefined, id: string, scale: Exclude<PlotScale, "share">): AxisInfo {
  // `visible` is dropped rather than set true: absent is drawn, and the store
  // collapses an absent field, so writing `true` would make a save and its
  // reload differ.
  const { visible: _visible, ...rest } = base ?? { id, side: "right" as const, autoRange: "always" as const };
  return {
    ...rest,
    id,
    side: scale === "left" ? "left" : "right",
    ...(scale === "hidden" ? { visible: false } : {}),
  };
}

/** The axis a plot is drawn against: its `axisRef`, else the pane's first. */
function axisOf(pane: Pane, plot: Plot): AxisInfo | undefined {
  return pane.axes?.find((a) => a.id === plot.axisRef) ?? pane.axes?.[0];
}

/**
 * The group with the plots moved. Returns `group` by identity when nothing can
 * move: no such plots, a target that does not exist, or the pane they are
 * already in with nothing to change.
 */
export function movePlotsToPane(
  group: ChartGroup,
  move: MoveToPane,
  mintId: () => string,
): ChartGroup {
  const asked = new Set(move.plotIds);
  const source = group.panes.find((p) => (p.plots ?? []).some((plot) => asked.has(plot.id)));
  if (!source) return group;
  const ids = withSmoothers(source, asked);
  const moving = (source.plots ?? []).filter((plot) => ids.has(plot.id));
  const toNew = move.target === NEW_PANE;
  const target = toNew ? undefined : group.panes.find((p) => p.id === move.target);
  if (!toNew && !target) return group;
  if (target === source) return group;

  // The scale the lines take with them, when they keep one. Into a new pane it
  // is drawn again, whatever it was — a pane whose only axis is hidden sits
  // flush against its edge with nothing to read the lines against.
  const carried = moving.map((plot) => axisOf(source, plot)).find(Boolean);
  // Lines on the source pane's *first* axis have no scale of their own — that
  // one belongs to the candles or the pane — so the catalog's stands in.
  const ownAxis = carried && carried === source.axes?.[0] && move.base ? move.base : carried;
  const newAxis: AxisInfo | undefined = toNew
    ? ownAxis
      ? shapedAxis(ownAxis, mintId(), ownAxis.side === "left" ? "left" : "right")
      : undefined
    : move.scale !== "share"
      ? shapedAxis(ownAxis, mintId(), move.scale)
      : undefined;
  const sharedAxisId = target?.axes?.[0]?.id;
  const axisRef = newAxis?.id ?? sharedAxisId;
  const moved: Plot[] = moving.map((plot) =>
    // A plot drawn against no axis (a signal lane) keeps none.
    plot.axisRef === undefined && !newAxis ? plot : { ...plot, axisRef },
  );

  const left = (source.plots ?? []).filter((plot) => !ids.has(plot.id));
  const used = new Set(left.map((plot) => plot.axisRef ?? source.axes?.[0]?.id));
  const remaining: Pane | undefined =
    left.length === 0 && source.role === "indicator"
      ? undefined
      : {
          ...source,
          plots: left,
          // A scale only the moved lines read goes with them; the first axis of
          // the price pane stays, since overlays and candles hang off it.
          axes: (source.axes ?? []).filter(
            (axis, i) => used.has(axis.id) || (i === 0 && source.role !== "indicator"),
          ),
        };

  const panes: Pane[] = [];
  for (const pane of group.panes) {
    if (pane === source) {
      if (remaining) panes.push(remaining);
      if (toNew) {
        // Right below where the lines came from, where the eye already is.
        panes.push({
          id: mintId(),
          role: "indicator",
          order: 0,
          height: "1fr",
          axes: newAxis ? [newAxis] : [],
          plots: moved,
          annotations: [],
        });
      }
      continue;
    }
    if (pane === target) {
      panes.push({
        ...pane,
        axes: newAxis ? [...(pane.axes ?? []), newAxis] : pane.axes,
        plots: [...(pane.plots ?? []), ...moved],
      });
      continue;
    }
    panes.push(pane);
  }
  return { ...group, panes: panes.map((pane, order) => ({ ...pane, order })) };
}

/**
 * Re-scale lines where they already are — the row's ⚙ (owner, 2026-10-05).
 *
 * `share` puts them on the pane's first axis; anything else gives them an axis
 * of their own, reshaping the one they have or minting one when they shared.
 * An axis no plot reads any more goes, except the pane's first, which the
 * candles and every sharing overlay hang off. Returns `group` by identity when
 * the plots are not found.
 */
export function setPlotsScale(
  group: ChartGroup,
  plotIds: readonly string[],
  scale: PlotScale,
  mintId: () => string,
  /** The catalog's axis for these lines (`axisFor`), used when one is minted. */
  base?: AxisInfo,
): ChartGroup {
  const asked = new Set(plotIds);
  const pane = group.panes.find((p) => (p.plots ?? []).some((plot) => asked.has(plot.id)));
  if (!pane) return group;
  const ids = withSmoothers(pane, asked);
  const first = pane.axes?.[0];
  const current = (pane.plots ?? []).filter((plot) => ids.has(plot.id)).map((plot) => axisOf(pane, plot)).find(Boolean);
  const own = current && current !== first ? current : undefined;

  let axes = [...(pane.axes ?? [])];
  let axisRef: string | undefined;
  if (scale === "share") {
    axisRef = first?.id;
  } else if (own && !sharedWithOthers(pane, own.id, ids)) {
    const reshaped = shapedAxis(own, own.id, scale);
    axes = axes.map((a) => (a.id === own.id ? reshaped : a));
    axisRef = own.id;
  } else if (own) {
    // **Shared with another indicator** (merged, below): reshaping it in place
    // would move that one too, so these lines take a copy of it instead —
    // same range and format, the side or visibility they asked for.
    const copy = shapedAxis(own, mintId(), scale);
    axes.push(copy);
    axisRef = copy.id;
  } else {
    // They shared the first axis, so there is no scale of theirs to copy: the
    // catalog's (a Rank's fixed 0–100), else a fresh auto-ranging one — never a
    // copy of the price axis's format and band.
    const minted = shapedAxis(base, mintId(), scale);
    axes.push(minted);
    axisRef = minted.id;
  }

  const plots = (pane.plots ?? []).map((plot) =>
    ids.has(plot.id) && (plot.axisRef !== undefined || axisRef !== first?.id) ? { ...plot, axisRef } : plot,
  );
  const used = new Set(plots.map((plot) => plot.axisRef ?? first?.id));
  axes = axes.filter((a, i) => i === 0 || used.has(a.id));
  return {
    ...group,
    panes: group.panes.map((p) => (p === pane ? { ...pane, axes, plots } : p)),
  };
}

/** A merged scale, by the axis it is: `with:<axisId>`. */
export type ScaleChoice = PlotScale | `with:${string}`;

/** Whether plots outside `ids` in this pane also draw on `axisId`. */
function sharedWithOthers(pane: Pane, axisId: string, ids: ReadonlySet<string>): boolean {
  const first = pane.axes?.[0]?.id;
  return (pane.plots ?? []).some((p) => !ids.has(p.id) && (p.axisRef ?? first) === axisId);
}

/**
 * The scale lines currently have in their pane, for the ⚙ select — `with:` an
 * axis when another indicator draws on it too (merged), else how theirs looks.
 */
export function plotsScale(group: ChartGroup, plotIds: readonly string[]): ScaleChoice | undefined {
  const ids = new Set(plotIds);
  const pane = group.panes.find((p) => (p.plots ?? []).some((plot) => ids.has(plot.id)));
  if (!pane) return undefined;
  const plot = (pane.plots ?? []).find((p) => ids.has(p.id))!;
  const axis = axisOf(pane, plot);
  if (!axis || axis === pane.axes?.[0]) return "share";
  if (sharedWithOthers(pane, axis.id, ids)) return `with:${axis.id}`;
  if (axis.visible === false) return "hidden";
  return axis.side === "left" ? "left" : "right";
}

/**
 * The other scales these lines could merge into: every axis in their pane, bar
 * the first, that some *other* plot draws on — with that plot's ids so the
 * caller can name it ("With Rank W").
 */
export function scalePartners(
  group: ChartGroup,
  plotIds: readonly string[],
): { axisId: string; plotIds: string[] }[] {
  const ids = new Set(plotIds);
  const pane = group.panes.find((p) => (p.plots ?? []).some((plot) => ids.has(plot.id)));
  if (!pane) return [];
  const first = pane.axes?.[0]?.id;
  const mine = axisOf(pane, (pane.plots ?? []).find((p) => ids.has(p.id))!)?.id;
  const out = new Map<string, string[]>();
  for (const p of pane.plots ?? []) {
    const axis = p.axisRef ?? first;
    if (ids.has(p.id) || !axis || axis === first || axis === mine) continue;
    out.set(axis, [...(out.get(axis) ?? []), p.id]);
  }
  return [...out].map(([axisId, plotIds]) => ({ axisId, plotIds }));
}

/**
 * Merge lines onto another indicator's scale (owner, 2026-10-06: two Ranks on
 * the price pane, one 0–100 axis). The axis they leave goes if nothing else
 * reads it. Returns `group` by identity when the plots or the axis are not
 * found in one pane.
 */
export function joinScale(group: ChartGroup, plotIds: readonly string[], axisId: string): ChartGroup {
  const asked = new Set(plotIds);
  const found = group.panes.find((p) => (p.plots ?? []).some((plot) => asked.has(plot.id)));
  if (!found || !found.axes?.some((a) => a.id === axisId)) return group;
  const pane = found;
  const paneAxes = found.axes;
  const ids = withSmoothers(pane, asked);
  const first = paneAxes[0]!.id;
  const plots = (pane.plots ?? []).map((p) => (ids.has(p.id) ? { ...p, axisRef: axisId } : p));
  const used = new Set(plots.map((p) => p.axisRef ?? first));
  const axes = paneAxes.filter((a, i) => i === 0 || used.has(a.id));
  return { ...group, panes: group.panes.map((p) => (p === pane ? { ...pane, axes, plots } : p)) };
}

/**
 * An axis in `pane`, bar the first, that a new scale like `like` should merge
 * into: same fixed range, already drawn on. Two Ranks share one 0–100 axis
 * this way rather than printing the same numbers twice (owner: automatic).
 */
export function matchingScale(pane: Pane, like: AxisInfo): AxisInfo | undefined {
  const range = like.visibleRange;
  if (like.autoRange === "always" || range?.min === undefined || range.max === undefined) return undefined;
  const first = pane.axes?.[0]?.id;
  const used = new Set((pane.plots ?? []).map((p) => p.axisRef ?? first));
  return pane.axes?.slice(1).find(
    (a) =>
      used.has(a.id) &&
      a.autoRange !== "always" &&
      a.visibleRange?.min === range.min &&
      a.visibleRange?.max === range.max,
  );
}

/**
 * The lines' own axis inside a pane they share, or `undefined` when they draw
 * on the pane's first — the axis the row's **Range** control fixes.
 */
export function ownAxisOf(group: ChartGroup, plotIds: readonly string[]): AxisInfo | undefined {
  const ids = new Set(plotIds);
  const pane = group.panes.find((p) => (p.plots ?? []).some((plot) => ids.has(plot.id)));
  if (!pane) return undefined;
  const axis = axisOf(pane, (pane.plots ?? []).find((p) => ids.has(p.id))!);
  return axis && axis !== pane.axes?.[0] ? axis : undefined;
}
